import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface GovernanceStatus {
  readonly bcp: { total: number; approved: number; draft: number; reviewOverdue: number; tier1WithoutApproval: number };
  readonly drPlan: { total: number; approved: number; testOverdue: number };
  readonly objectives: { total: number; approved: number; breached: number };
  readonly drTests: { lastPassedAt: string | null; failedLast12m: number };
  readonly drReadinessMinScore: number;
  readonly compliant: boolean;
}

interface BcpRow {
  readonly bcpId: string;
  readonly bcpCode: string;
  readonly bcpName: string;
  readonly businessFunction: string;
  readonly criticalityTier: string;
  readonly status: string;
}

const TIERS = ['TIER_1', 'TIER_2', 'TIER_3'];

/**
 * M31 — Continuity Governance Dashboard (§31 / §24). Compliance posture across
 * the BCM programme: BCP coverage + overdue reviews (BR-07), Tier-1 services
 * lacking an approved BCP (BR-01), DR plans overdue for testing (BR-04),
 * breached recovery objectives and recent DR-test outcomes — with an overall
 * compliant / non-compliant verdict and the LAW-6 DR-readiness floor. Also hosts
 * the Business Continuity Plan register: register a BCP and drive its lifecycle
 * (submit for review, approve, record review), each wired to the guarded
 * /v1/bcm/bcps write API.
 */
export function ContinuityGovernancePage(): ReactNode {
  const { t: tr } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { token } = useAuth();
  const [data, setData] = useState<GovernanceStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [bcps, setBcps] = useState<BcpRow[]>([]);
  const [bcpError, setBcpError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newFunction, setNewFunction] = useState('');
  const [newTier, setNewTier] = useState('TIER_1');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GovernanceStatus }>('/v1/bcm/governance/status', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: BcpRow[] }>('/v1/bcm/bcps', { token })
      .then((r) => { if (alive) { setBcps(r.data); setBcpError(null); } })
      .catch((e) => { if (alive) setBcpError((e as Error).message); });
    return () => { alive = false; };
  }, [token, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m31.governance.err403')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m31.governance.errName')); return; }
    if (!newFunction.trim()) { setActionError(tr('m31.governance.errFunction')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/bcps`, {
        method: 'POST',
        body: { bcpName: newName.trim(), businessFunction: newFunction.trim(), criticalityTier: newTier },
        token,
      });
      setNotice(tr('m31.governance.noticeRegistered', { name: newName.trim() }));
      setNewName('');
      setNewFunction('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BcpRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/bcps/${r.bcpId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(tr('m31.governance.actNotice', { code: r.bcpCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="continuity-governance">
      <h1>{tr('m31.governance.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{tr('m31.governance.loading')}</p>
      ) : (
        <>
          <p aria-label="compliance-verdict" data-compliant={data.compliant ? 'yes' : 'no'}>
            {data.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
          </p>
          <p aria-label="dr-readiness-floor">{tr('m31.governance.drFloor', { score: data.drReadinessMinScore })}</p>
          <section aria-label="governance-cards" className="card-grid">
            <article aria-label="card-bcp">
              <h2>{tr('m31.governance.cardBcpTitle')}</h2>
              <p aria-label="bcp-total">{tr('m31.governance.bcpTotal', { count: data.bcp.total })}</p>
              <p aria-label="bcp-approved">{tr('m31.governance.bcpApproved', { count: data.bcp.approved })}</p>
              <p aria-label="bcp-draft">{tr('m31.governance.bcpDraft', { count: data.bcp.draft })}</p>
              <p aria-label="bcp-review-overdue">{tr('m31.governance.bcpReviewOverdue', { count: data.bcp.reviewOverdue })}</p>
              <p aria-label="bcp-tier1-gap">{tr('m31.governance.bcpTier1Gap', { count: data.bcp.tier1WithoutApproval })}</p>
            </article>
            <article aria-label="card-dr-plans">
              <h2>{tr('m31.governance.cardDrTitle')}</h2>
              <p aria-label="dr-total">{tr('m31.governance.drTotal', { count: data.drPlan.total })}</p>
              <p aria-label="dr-approved">{tr('m31.governance.drApproved', { count: data.drPlan.approved })}</p>
              <p aria-label="dr-test-overdue">{tr('m31.governance.drTestOverdue', { count: data.drPlan.testOverdue })}</p>
            </article>
            <article aria-label="card-objectives">
              <h2>{tr('m31.governance.cardObjTitle')}</h2>
              <p aria-label="obj-total">{tr('m31.governance.objTotal', { count: data.objectives.total })}</p>
              <p aria-label="obj-approved">{tr('m31.governance.objApproved', { count: data.objectives.approved })}</p>
              <p aria-label="obj-breached">{tr('m31.governance.objBreached', { count: data.objectives.breached })}</p>
            </article>
            <article aria-label="card-dr-tests">
              <h2>{tr('m31.governance.cardTestTitle')}</h2>
              <p aria-label="test-last-passed">{data.drTests.lastPassedAt ? tr('m31.governance.lastPassed', { date: atDay(data.drTests.lastPassedAt) }) : tr('m31.governance.noPassingDrill')}</p>
              <p aria-label="test-failed-12m">{tr('m31.governance.failed12m', { count: data.drTests.failedLast12m })}</p>
            </article>
          </section>

          <section aria-label="bcp-register">
            <h2>{tr('m31.governance.registerHeading')}</h2>
            <form aria-label="bcp-form" onSubmit={(e) => void create(e)}>
              <label>{tr('m31.governance.fieldName')}<input aria-label="field-bcp-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
              <label>{tr('m31.governance.fieldFunction')}<input aria-label="field-business-function" value={newFunction} onChange={(e) => setNewFunction(e.target.value)} /></label>
              <label>{tr('m31.governance.fieldTier')}
                <select aria-label="field-tier" value={newTier} onChange={(e) => setNewTier(e.target.value)}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <button type="submit" aria-label="submit-bcp" disabled={busy}>{busy ? tr('m31.governance.busyRegister') : tr('m31.governance.submitRegister')}</button>
            </form>
            {actionError && <p role="alert" aria-label="bcp-error">{actionError}</p>}
            {notice && <p aria-label="bcp-notice">{notice}</p>}
            {bcpError && <p role="alert" aria-label="bcp-list-error">{bcpError}</p>}
            <p aria-label="bcp-count">{tr('m31.governance.bcpCount', { count: bcps.length })}</p>
            <table aria-label="bcp-table">
              <thead><tr><th>{tr('m31.governance.thCode')}</th><th>{tr('m31.governance.thName')}</th><th>{tr('m31.governance.thFunction')}</th><th>{tr('m31.governance.thTier')}</th><th>{tr('m31.governance.thStatus')}</th><th>{tr('m31.governance.thActions')}</th></tr></thead>
              <tbody>
                {bcps.map((r) => (
                  <tr key={r.bcpId}>
                    <td>{r.bcpCode}</td><td>{r.bcpName}</td><td>{r.businessFunction}</td>
                    <td>{r.criticalityTier}</td><td>{r.status}</td>
                    <td>
                      {r.status === 'DRAFT' && (
                        <button type="button" aria-label={`submit-bcp-${r.bcpCode}`} onClick={() => void act(r, 'submit', tr('m31.governance.submitted'))}>{tr('m31.governance.submit')}</button>
                      )}
                      {r.status === 'UNDER_REVIEW' && (
                        <button type="button" aria-label={`approve-bcp-${r.bcpCode}`} onClick={() => void act(r, 'approve', tr('m31.governance.approvedLabel'))}>{tr('m31.governance.approveBtn')}</button>
                      )}
                      {r.status === 'APPROVED' && (
                        <button type="button" aria-label={`review-bcp-${r.bcpCode}`} onClick={() => void act(r, 'review', tr('m31.governance.reviewRecorded'))}>{tr('m31.governance.recordReview')}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}
